import { useState, useEffect, useMemo } from "react";
import { ExtractedContract } from "../../types/contract";
import {
  getDisplayValue,
  isSourceValue,
  isCoOccupantInfo,
} from "../../utils/formatter";

interface UseDirtyStateParams {
  selectedContract: ExtractedContract | null;
  editableContracts: ExtractedContract[];
  setEditableContracts: React.Dispatch<React.SetStateAction<ExtractedContract[]>>;
  setIsDirty: (dirty: boolean) => void;
}

export const useDirtyState = ({
  selectedContract,
  editableContracts,
  setEditableContracts,
  setIsDirty,
}: UseDirtyStateParams) => {
  const [changedContractIds, setChangedContractIds] = useState<Set<number>>(
    new Set()
  );
  const [isEditing, setIsEditing] = useState(false);
  const [originalSelectedContract, setOriginalSelectedContract] =
    useState<ExtractedContract | null>(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingNavigationAction, setPendingNavigationAction] =
    useState<(() => void) | null>(null);

  useEffect(() => {
    // When the selected contract changes, reset isEditing state
    setIsEditing(false);
  }, [selectedContract]);

  const selectedEditableContract = useMemo(() => {
    if (!selectedContract) return null;
    // Find the contract by ID to ensure we get the exact selected contract
    return (
      editableContracts.find((c) => c.id === selectedContract.id) || null
    );
  }, [selectedContract, editableContracts]);

  // --- Dirty State Handling ---
  const hasUnsavedChanges =
    isEditing &&
    !!selectedEditableContract?.id &&
    changedContractIds.has(selectedEditableContract.id);

  // Update global dirty state and handle browser-level navigation blocking
  useEffect(() => {
    setIsDirty(hasUnsavedChanges);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = ""; // For modern browsers
    };

    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    // Cleanup on unmount or when hasUnsavedChanges becomes false
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Also ensure the global state is clean when this component is no longer relevant
      setIsDirty(false);
    };
  }, [hasUnsavedChanges, setIsDirty]);

  const confirmAndNavigate = (navigationAction: () => void) => {
    if (hasUnsavedChanges) {
      setPendingNavigationAction(() => navigationAction);
      setShowUnsavedChangesModal(true);
    } else {
      navigationAction();
    }
  };

  const handleStartEdit = () => {
    if (selectedEditableContract) {
      setOriginalSelectedContract(JSON.parse(JSON.stringify(selectedEditableContract))); // Deep copy
      setIsEditing(true);
    }
  };

  const exitEditMode = () => {
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    if (originalSelectedContract) {
      // Revert changes for the specific aggregated contract group
      const newEditableContracts = editableContracts.map((c) => {
        if (
          c.property_name === originalSelectedContract.property_name &&
          c.room_name === originalSelectedContract.room_name &&
          c.lessee_name === originalSelectedContract.lessee_name
        ) {
          return JSON.parse(JSON.stringify(originalSelectedContract)); // Revert to original
        }
        return c;
      });
      setEditableContracts(newEditableContracts);
      // Explicitly remove the selected contract's ID from changedContractIds upon cancel
      if (selectedContract?.id !== undefined) {
        setChangedContractIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(selectedContract.id!);
          return newSet;
        });
      }
    }
    setIsEditing(false);
  };

  const detailFields: (keyof ExtractedContract)[] = [
    // 契約者情報
    "contractor_name",
    "contractor_gender",
    "contractor_work_info",
    "co_occupant_info",
    "emergency_contact_info",
    // 契約情報
    "rent",
    "management_fee",
    "contract_start_date",
    "contract_end_date",
    "renewal_fee_basis",
    "renewal_fee_value",
    "renewal_fee_period",
    "renewal_admin_fee",
    "guarantee_company_name",
    "guarantee_number",
    "insurance_company_info",
    "joint_guarantor_info",
    // その他
    "pet_info",
    "vehicle_info",
    "key_info",
    // 建物情報
    "property_address",
    "property_address2",
    "property_postal_code",
    "building_code",
    "building_name_kana",
    // 家主情報
    "owner_name",
    "owner_name_kana",
    "owner_type",
    "owner_company_name",
    "owner_postal_code",
    "owner_address1",
    "owner_address2",
    "owner_home_phone",
    "owner_mobile_phone",
    "owner_bank_1_branch_code",
    "owner_bank_1_account_type",
    "owner_bank_1_account_number",
    "owner_bank_1_account_holder_name",
    "owner_bank_2_branch_code",
    "owner_bank_2_account_type",
    "owner_bank_2_account_number",
    "owner_bank_2_account_holder_name",
  ];

  const labels: Record<string, string> = {
    // 契約者情報
    contractor_name: "契約者名",
    contractor_gender: "契約者性別",
    contractor_work_info: "契約者勤務先",
    co_occupant_info: "同居者情報",
    emergency_contact_info: "緊急連絡先",
    // 契約情報
    rent: "賃料",
    management_fee: "管理費",
    contract_start_date: "契約開始日",
    contract_end_date: "契約終了日",
    renewal_fee_basis: "更新料(基準)",
    renewal_fee_value: "更新料(値)",
    renewal_fee_period: "更新料(期間)",
    renewal_admin_fee: "更新事務手数料",
    guarantee_company_name: "保証会社名",
    guarantee_number: "保証番号",
    insurance_company_info: "保険会社情報",
    joint_guarantor_info: "連帯保証人情報",
    // その他
    pet_info: "ペット情報",
    vehicle_info: "車両情報",
    key_info: "鍵情報",
    // 建物情報
    property_address: "物件住所1",
    property_address2: "物件住所2",
    property_postal_code: "物件郵便番号",
    building_code: "建物コード",
    building_name_kana: "建物名フリガナ",
    // 家主情報
    owner_name: "家主名",
    owner_name_kana: "家主名フリガナ",
    owner_type: "家主種別",
    owner_company_name: "家主会社名",
    owner_postal_code: "家主郵便番号",
    owner_address1: "家主住所1",
    owner_address2: "家主住所2",
    owner_home_phone: "家主自宅Tel",
    owner_mobile_phone: "家主携帯電話番号",
    owner_bank_1_branch_code: "銀行支店コード1",
    owner_bank_1_account_type: "銀行区分1",
    owner_bank_1_account_number: "銀行番号1",
    owner_bank_1_account_holder_name: "銀行名義1",
    owner_bank_2_branch_code: "銀行支店コード2",
    owner_bank_2_account_type: "銀行区分2",
    owner_bank_2_account_number: "銀行番号2",
    owner_bank_2_account_holder_name: "銀行名義2",
  };

  const getEditableFieldValue = (item: ExtractedContract, key: keyof ExtractedContract): string => {
    // Re-use the robust getDisplayValue from formatter.ts
    return getDisplayValue(item, key);
  };

  // Helper function to compare two ExtractedContract objects for changes
  const hasContractChanges = (
    current: ExtractedContract,
    original: ExtractedContract
  ): boolean => {
    for (const field of detailFields) {
      if (field === "co_occupant_info") {
        // Co-occupant info is complex, implement deeper comparison if needed, or skip
        continue;
      }

      const currentFieldValue = getEditableFieldValue(current, field);
      const originalFieldValue = getEditableFieldValue(original, field);

      if (currentFieldValue !== originalFieldValue) {
        return true;
      }
    }
    return false;
  };

  const handleDetailChange = (
    field: keyof ExtractedContract,
    value: string
  ) => {
    if (!selectedContract || selectedContract.id === undefined) return;
    if (!originalSelectedContract) {
      console.warn("originalSelectedContract is null when editing. This should not happen.");
      return;
    }

    let updatedContractId: number | undefined;

    const newEditableContracts = editableContracts.map((c) => {
      // Find the specific contract by ID
      if (c.id === selectedContract.id) {
        const updatedContract = { ...c };
        const fieldValue = updatedContract[field];

        if (isCoOccupantInfo(fieldValue)) {
          // Co-occupant info is complex and might need a dedicated UI.
          // For now, we assume it's not directly editable via a simple text field.
        } else if (isSourceValue(fieldValue)) {
          fieldValue.value = value;
        } else {
          // If the field is not a SourceValue object, create one.
          (updatedContract as any)[field] = {
            value: value,
            source_document: "手動入力",
          };
        }
        updatedContractId = updatedContract.id; // Capture the ID of the contract being updated
        return updatedContract;
      }
      return c;
    });

    setEditableContracts(newEditableContracts);

    // After updating editableContracts, check if the specific contract group has changed from its original state
    if (updatedContractId !== undefined) {
      const currentContractState = newEditableContracts.find(c => c.id === updatedContractId);
      if (currentContractState) {
        if (hasContractChanges(currentContractState, originalSelectedContract)) {
          setChangedContractIds((prev) => new Set(prev).add(updatedContractId!));
        } else {
          setChangedContractIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(updatedContractId!);
            return newSet;
          });
        }
      }
    }
  };

  return {
    changedContractIds,
    setChangedContractIds,
    isEditing,
    exitEditMode,
    originalSelectedContract,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    pendingNavigationAction,
    setPendingNavigationAction,
    selectedEditableContract,
    hasUnsavedChanges,
    confirmAndNavigate,
    handleStartEdit,
    handleCancelEdit,
    handleDetailChange,
    detailFields,
    labels,
    getEditableFieldValue,
  };
};
